import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslations } from '../hooks/useTranslations';
import { Linkedin, Instagram, Star } from 'lucide-react';
import { SiTiktok, SiReddit } from 'react-icons/si';
import { getArticleBySlug, getLocalizedBlogArticlePath } from '../blog/articles';
import {
  AUDIO_TRANSLATION_LANDING_PATH,
  getAudioTranslationFooterLabel,
} from '../content/audioTranslation';
import { getCaptionsLandingFooterLabel } from '../content/captionsLanding';
import { CREATORS_LANDING_PATH, getCreatorsLandingLabel } from '../content/creatorsLanding';
import {
  FREE_CAPTIONS_LANDING_PATH,
  getBasePathFromUrlPath,
  getBlogArticleSlugFromUrlPath,
  getLocalizedPath,
  type LocalizedBasePath,
} from '../utils/localizedRoutes';
import './Footer.css';

export function Footer({ onShowBlog }: { onShowBlog?: () => void } = {}) {
  const location = useLocation();
  const { t, currentLanguage, availableLanguages } = useTranslations();
  const currentBasePath = getBasePathFromUrlPath(location.pathname);
  const blogPath = getLocalizedPath('/blog', currentLanguage);
  const creatorsLandingPath = getLocalizedPath(CREATORS_LANDING_PATH, currentLanguage);
  const freeCaptionsPath = getLocalizedPath(FREE_CAPTIONS_LANDING_PATH, currentLanguage);
  const audioTranslationPath = getLocalizedPath(AUDIO_TRANSLATION_LANDING_PATH, currentLanguage);
  const privacyPath = getLocalizedPath('/privacy', currentLanguage);
  const termsPath = getLocalizedPath('/terms', currentLanguage);
  const cookiePolicyPath = getLocalizedPath('/cookie-policy', currentLanguage);
  const creatorsLandingLabel = getCreatorsLandingLabel(currentLanguage);
  const freeCaptionsLabel = getCaptionsLandingFooterLabel(currentLanguage);
  const audioTranslationLabel = getAudioTranslationFooterLabel(currentLanguage);

  const languageNames: Record<string, string> = {
    en: 'English',
    ru: 'Русский',
    es: 'Español',
    fr: 'Français',
    de: 'Deutsch',
    it: 'Italiano',
    pt: 'Português',
    pl: 'Polski',
    ja: '日本語',
    ko: '한국어',
    zh: '中文',
    ar: 'العربية',
    bg: 'Български',
    hr: 'Hrvatski',
    cs: 'Čeština',
    da: 'Dansk',
    nl: 'Nederlands',
    et: 'Eesti',
    fi: 'Suomi',
    el: 'Ελληνικά',
    hu: 'Magyar',
    ga: 'Gaeilge',
    lv: 'Latviešu',
    lt: 'Lietuvių',
    mt: 'Malti',
    ro: 'Română',
    sk: 'Slovenčina',
    sl: 'Slovenščina',
    sv: 'Svenska',
  };

  const languageButtonOrder = [
    ...availableLanguages.filter((language) => language !== 'ru'),
    ...availableLanguages.filter((language) => language === 'ru'),
  ];

  const buildLocalizedFooterHref = (language: string) => {
    const targetLanguage = language as typeof currentLanguage;
    const currentArticleSlug = getBlogArticleSlugFromUrlPath(location.pathname);

    if (currentArticleSlug) {
      const currentArticle = getArticleBySlug(currentArticleSlug, currentLanguage);
      if (currentArticle) {
        const localizedArticlePath = getLocalizedBlogArticlePath(currentArticle.id, targetLanguage);
        if (localizedArticlePath) {
          return `https://vevi.monster${localizedArticlePath}`;
        }
      }
    }

    const basePath: LocalizedBasePath = currentBasePath || "/";
    const localizedPath = getLocalizedPath(basePath, targetLanguage);
    return `https://vevi.monster${localizedPath}`;
  };

  const languagePageLinks = languageButtonOrder.map((language) => {
    return {
      language,
      label: languageNames[language] || language.toUpperCase(),
      href: buildLocalizedFooterHref(language),
    };
  });

  return (
    <footer className="vevi-footer">
      <div className="vevi-footer-container">
        <div className="vevi-footer-main">
          <section className="vevi-footer-column">
            <h3 className="vevi-footer-title">Big Mood</h3>
            <ul className="vevi-footer-list">
              <li className="vevi-footer-list-item">
                <a
                  href={`https://vevi.monster${blogPath}`}
                  className="vevi-footer-link"
                  onClick={() => onShowBlog?.()}
                >
                  Breaking News
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href={`https://vevi.monster${creatorsLandingPath}`}
                  className="vevi-footer-link"
                >
                  {creatorsLandingLabel}
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href={`https://vevi.monster${freeCaptionsPath}`}
                  className="vevi-footer-link"
                >
                  {freeCaptionsLabel}
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href={`https://vevi.monster${audioTranslationPath}`}
                  className="vevi-footer-link"
                >
                  {audioTranslationLabel}
                </a>
              </li>
            </ul>
          </section>

          <section className="vevi-footer-column">
            <h3 className="vevi-footer-title">No jokes</h3>
            <ul className="vevi-footer-list">
              <li className="vevi-footer-list-item">
                <Link to={privacyPath} className="vevi-footer-link">
                  {t.footer?.privacyPolicy || 'Privacy Policy'}
                </Link>
              </li>
              <li className="vevi-footer-list-item">
                <Link to={termsPath} className="vevi-footer-link">
                  {t.footer?.termsOfUse || 'Terms of Use'}
                </Link>
              </li>
              <li className="vevi-footer-list-item">
                <Link to={cookiePolicyPath} className="vevi-footer-link">
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </section>

          <section className="vevi-footer-column">
            <h3 className="vevi-footer-title">Fan Pages</h3>
            <ul className="vevi-footer-list">
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.linkedin.com/company/vevi-monster/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-social-link"
                >
                  <Linkedin className="vevi-footer-social-icon" />
                  <span>LinkedIn</span>
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.instagram.com/vevimonster"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-social-link"
                >
                  <Instagram className="vevi-footer-social-icon" />
                  <span>Instagram</span>
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.tiktok.com/@vevi.monster"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-social-link"
                >
                  <SiTiktok className="vevi-footer-social-icon" />
                  <span>TikTok</span>
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.reddit.com/r/vevimonster/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-social-link"
                >
                  <SiReddit className="vevi-footer-social-icon" />
                  <span>Reddit</span>
                </a>
              </li>
            </ul>
          </section>

          <section className="vevi-footer-column">
            <h3 className="vevi-footer-title">Fan Love</h3>
            <ul className="vevi-footer-list">
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.trustpilot.com/review/vevi.monster"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-trustpilot-link"
                >
                  <span className="vevi-footer-trustpilot-name">Trustpilot</span>
                  <span className="vevi-footer-trustpilot-stars" aria-label="Five star rating">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <span key={index} className="vevi-footer-trustpilot-star-tile">
                        <Star className="vevi-footer-trustpilot-star" />
                      </span>
                    ))}
                  </span>
                </a>
              </li>
            </ul>
          </section>
        </div>

        <section className="vevi-footer-language-switchboard" aria-label="Localized page links">
          <h3 className="vevi-footer-title">New Language, Who Dis?</h3>
          <div className="vevi-footer-language-buttons">
            {languagePageLinks.map((item) => (
              <a
                key={item.language}
                href={item.href}
                className="vevi-footer-language-button"
              >
                {item.label}
              </a>
            ))}
          </div>
        </section>

        <div className="vevi-footer-bottom">
          <p className="vevi-footer-copyright">
            © 2026 VEVI Monster. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
